import { Link as GatsbyLink } from 'gatsby'
import React from 'react'
import { ChevronDown } from 'react-feather'
import styled from 'styled-components'
import { NavigationMenu } from '@base-ui/react/navigation-menu'

import { fontSizes } from '@utils/Typography'
import { AppContent } from './AppContent'
import { ThemeProvider } from '@/layouts'
import { LearningContent } from './LearningContent'
import { Flex } from '@primitives/Layout'

const StyledTrigger = styled(NavigationMenu.Trigger)(({ color }) => ({
  position: 'relative',
  userSelect: 'none',
  color,
  textAlign: 'center',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  paddingLeft: 8,
  paddingRight: 4,
  paddingTop: 8,
  paddingBottom: 8,
  borderRadius: 8,
  fontFamily: 'inherit',
  fontSize: fontSizes.body,
  lineHeight: 1,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 8,
    backgroundColor: color,
    opacity: 0,
    transition: 'opacity 120ms ease-out'
  },

  '&[data-popup-open], &:hover': {
    '&::before': { opacity: 0.05 },
    '.nav-menu__chevron': { transform: 'rotate(180deg)' }
  },

  '.nav-menu__chevron': {
    transition: 'transform 90ms ease-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}))

const StyledPopup = styled(NavigationMenu.Popup)({
  borderRadius: 16,
  boxShadow: '0 15px 25px -2px rgba(0,0,0,0.2)',
  backgroundColor: '#fff',
  opacity: 0,
  transform: 'translateY(8px)',
  transition: 'opacity 120ms ease-out, transform 120ms ease-out',

  '&[data-open]': {
    opacity: 1,
    transform: 'translateY(0)'
  }
})

const StyledViewport = styled(NavigationMenu.Viewport)({
  borderRadius: 16,
  overflow: 'hidden',
  // opacity: 0,
  transform: 'translateY(8px)',
  transition: 'opacity 120ms ease-out, transform 120ms ease-out',

  '&[data-open]': {
    opacity: 1,
    transform: 'translateY(0)'
  }
})

const AccountLinkWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  '.account__arrow': {
    transition: 'transform 90ms ease-out'
  },
  '&:hover': {
    '.account__arrow': {
      transform: 'translateX(4px)'
    }
  }
})

const StyledRoot = styled(NavigationMenu.Root)({
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  lineHeight: 1,
  backgroundColor: 'transparent'
})

const StyledList = styled(NavigationMenu.List)({
  display: 'flex',
  alignItems: 'center',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  gap: 16
})

const StyledItem = styled(NavigationMenu.Item)({
  listStyle: 'none'
})

const StyledAccountLinkWrapper = styled.div({
  paddingLeft: 8,
  paddingRight: 8,
  paddingTop: 8,
  paddingBottom: 8,
  fontSize: fontSizes.body,
  color: 'inherit',
  textDecoration: 'none',
  '& a': {
    color: 'inherit',
    textDecoration: 'none'
  }
})

const StyledPositioner = styled(NavigationMenu.Positioner)({
  zIndex: 9999
})

const NavLink = ({
  href,
  external,
  closeOnClick = true,
  children,
  ...props
}) => {
  if (external) {
    return (
      <NavigationMenu.Link
        href={href}
        closeOnClick={closeOnClick}
        render={linkProps => <a {...linkProps} rel="noopener" />}
        {...props}
      >
        {children}
      </NavigationMenu.Link>
    )
  }

  return (
    <NavigationMenu.Link
      href={href}
      closeOnClick={closeOnClick}
      render={linkProps => {
        const { href: to, ...rest } = linkProps
        return <GatsbyLink to={to} {...rest} />
      }}
      {...props}
    >
      {children}
    </NavigationMenu.Link>
  )
}

export const NavMenu = ({ color, theme, imageData }) => {
  return (
    <StyledRoot>
      <StyledList>
        <StyledItem value="apps">
          <StyledTrigger color={color}>
            Apps
            <NavigationMenu.Icon className="nav-menu__chevron">
              <ChevronDown width={18} height={18} color={color} />
            </NavigationMenu.Icon>
          </StyledTrigger>
          <NavigationMenu.Content>
            <Flex
              flexDirection="column"
              borderRadius={16}
              boxShadow="0 15px 25px -2px rgba(0,0,0,0.2)"
              backgroundColor="#fff"
              minWidth={200}
              width="min(400px, 100vw)"
              overflow="hidden"
            >
              <ThemeProvider>
                <AppContent imageData={imageData} theme={theme} />
              </ThemeProvider>
            </Flex>
          </NavigationMenu.Content>
        </StyledItem>

        <StyledItem value="learn">
          <StyledTrigger color={color}>
            Learning
            <NavigationMenu.Icon className="nav-menu__chevron">
              <ChevronDown width={18} height={18} color={color} />
            </NavigationMenu.Icon>
          </StyledTrigger>
          <NavigationMenu.Content>
            <ThemeProvider>
              <LearningContent imageData={imageData} theme={theme} />
            </ThemeProvider>
          </NavigationMenu.Content>
        </StyledItem>

        <StyledItem value="account">
          <StyledAccountLinkWrapper>
            <NavLink href="https://accounts.bonobolabs.com/" external>
              <AccountLinkWrapper>
                <span>Account</span>
                <Flex as="span" className="account__arrow">
                  →
                </Flex>
              </AccountLinkWrapper>
            </NavLink>
          </StyledAccountLinkWrapper>
        </StyledItem>
      </StyledList>

      <NavigationMenu.Portal>
        {/* Portalled to <body>, outside SharedLayout's GPU-ignore wrapper,
            so the popup opts out of the GPU compositor itself. */}
        <StyledPositioner
          data-gpu-ignore=""
          sideOffset={8}
          collisionPadding={{ top: 5, bottom: 5, left: 20, right: 20 }}
        >
          <StyledPopup>
            <StyledViewport />
          </StyledPopup>
        </StyledPositioner>
      </NavigationMenu.Portal>
    </StyledRoot>
  )
}
